import {
  Activity,
  Calendar,
  Files,
  FileText,
  Info,
  Route,
  Shield,
  Sparkles,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router";

import { useAuth } from "@/providers";
import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { EntityComments } from "@/shared/components/comments";
import { CreateDatasetButton } from "@/shared/components/CreateDatasetButton";
import {
  EntityTabs,
  type EntityTabItem,
  useActiveEntityTab,
} from "@/shared/components/EntityPage";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Banner } from "@/shared/foundation/primitives";
import { DatasetTab } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import styles from "./Dataset.module.css";
import { DatasetActionStatus } from "./DatasetActionStatus";
import { DatasetHeader } from "./DatasetHeader";
import type { DatasetContext } from "./useDatasetContext";

/**
 * Dataset detail page: parent layout for the `/datasets/:datasetPath` route
 * tree. Renders the page chrome (breadcrumbs, header, tab bar, comments) and
 * delegates the active tab's body to <Outlet>. Each child tab route reads
 * shared parent-derived values (datasetId, the dataset record, current org)
 * from outlet context via `useDatasetContext` instead of re-deriving them
 * from `useParams`/`useAuth`/`useDataset`.
 *
 * Failure modes:
 * - Missing `:datasetPath` route param: render an inline error and skip the
 *   <Outlet>; no child tab mounts.
 * - `useDataset` errors: rethrown via `throwOnError: true` and propagate up
 *   to the nearest router `errorElement`
 */
export function Dataset() {
  const params = useParams();
  const datasetId = params.datasetPath;
  const datasetQuery = useDataset(datasetId, { throwOnError: true });
  const { currentOrganization: org } = useAuth();

  useRegisterViewingContext("dataset", {
    datasetId,
    record: datasetQuery.data,
    orgId: datasetQuery.data?.org_id,
  });

  // Memoized so the object passed to <Outlet context={...}> keeps a stable
  // reference across renders that don't change any of its inputs. AuthProvider
  // returns a fresh `value` object on every render (see AuthProvider.tsx), so
  // without this memo each parent render would hand child tabs a new context
  // object and invalidate their downstream memos and effects that depend on
  // it by reference.
  const outletContext = useMemo<DatasetContext | null>(() => {
    if (!datasetId) {
      return null;
    }
    return {
      datasetId,
      org,
    };
  }, [datasetId, org]);

  // `outletContext` is null iff `datasetId` is falsy; the second clause is
  // redundant at runtime but narrows `outletContext` to non-null for the
  // <Outlet> below.
  if (!datasetId || !outletContext) {
    return (
      <PageLayout title={`Roboto - Dataset`}>
        <Banner variant="error">Missing dataset id in path</Banner>
      </PageLayout>
    );
  }

  const breadcrumbs = [
    <Link key="list" to="/datasets">
      Datasets
    </Link>,
    <ExpandableCopyText key="datasetId" text={datasetId} trimLength={15} />,
  ];

  return (
    <PageLayout
      title={`Roboto - Dataset - ${datasetId}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
      headerContentRight={<CreateDatasetButton />}
    >
      <ErrorBoundary
        fallback={(props) => {
          if (props.error instanceof Error) {
            return <Banner variant="error">{props.error.message}</Banner>;
          }

          return (
            <Banner variant="error">
              Something went wrong. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Banner>
          );
        }}
      >
        <section className={styles.root}>
          <div>
            <DatasetHeader datasetId={datasetId} />
            {/* Keyed so another dataset's strip starts over, rather than
                keep the Files folder last open in this one. */}
            <DatasetTabStrip key={datasetId} datasetId={datasetId} />
          </div>
          <Outlet context={outletContext} />
          <div className={styles.comments}>
            <EntityComments entityType={"dataset"} entityId={datasetId} />
          </div>
        </section>
      </ErrorBoundary>
    </PageLayout>
  );
}

/**
 * The dataset page's tabs, in three groups: what the dataset holds, what has
 * been run on it, and its record. Each link keeps the query string; Files
 * returns to the folder last open in it.
 */
function DatasetTabStrip({ datasetId }: { datasetId: string }) {
  const location = useLocation();
  // Same flag gates the sessions route in DatasetsRoutes.
  const sessionsTabEnabled = useCheckFeatureFlag("release-sessions");
  const [filesPath, setFilesPath] = useState<string>(DatasetTab.Files);

  const tabs: EntityTabItem<DatasetTab>[] = [
    { value: DatasetTab.Files, label: "Files", icon: <Files />, to: filesPath },
    { value: DatasetTab.Topics, label: "Topics", icon: <Activity /> },
    ...(sessionsTabEnabled
      ? [{ value: DatasetTab.Sessions, label: "Sessions", icon: <Route /> }]
      : []),
    { value: DatasetTab.Events, label: "Events", icon: <Calendar /> },
    "divider",
    {
      value: DatasetTab.Actions,
      label: "Actions",
      icon: <Zap />,
      endAdornment: <DatasetActionStatus datasetId={datasetId} />,
    },
    { value: DatasetTab.AgentThreads, label: "Agents", icon: <Sparkles /> },
    "divider",
    { value: DatasetTab.AISummary, label: "Summary", icon: <FileText /> },
    { value: DatasetTab.Details, label: "Details", icon: <Info /> },
    { value: DatasetTab.Permissions, label: "Permissions", icon: <Shield /> },
  ];
  const activeTab = useActiveEntityTab(tabs);

  // Files owns deeper paths (its folders); remember the one open in it so the
  // tab returns there.
  useEffect(() => {
    if (activeTab === DatasetTab.Files) {
      setFilesPath(location.pathname);
    }
  }, [activeTab, location.pathname]);

  return (
    <EntityTabs tabs={tabs} activeTab={activeTab} aria-label="dataset tabs" />
  );
}
